import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Avatar from "../components/Avatar";
import PerformanceBadge from "../components/PerformanceBadge";
import { FULL_MONTHS, thisMonth, thisYear, classNames } from "../lib/utils";
import { Trophy, Crown } from "lucide-react";

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ rows: [] });
  const [homes, setHomes] = useState([]);
  const hmHomes = (user.assigned_homes && user.assigned_homes.length ? user.assigned_homes : (user.assigned_home ? [user.assigned_home] : []));
  const isMultiHomeHM = user.access_role === "House Manager" && hmHomes.length > 1;
  const [home, setHome] = useState(user.access_role === "House Manager" ? (isMultiHomeHM ? "ALL" : (hmHomes[0] || "")) : "ALL");
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(thisYear());
  const [view, setView] = useState("monthly");

  useEffect(() => { api.get("/homes").then(({ data }) => setHomes(data)); }, []);
  useEffect(() => {
    api.get("/leaderboard", { params: { home, month, year, view } }).then(({ data }) => setData(data));
  }, [home, month, year, view]);

  const title = view === "annual"
    ? `${year} Annual — ${home === "ALL" ? "All Homes" : home} Leaderboard`
    : `${FULL_MONTHS[month - 1]} ${year} — ${home === "ALL" ? "All Homes" : home} Leaderboard`;

  const top3 = data.rows.slice(0, 3);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div>
        <p className="text-small-caps">Recognition</p>
        <h1 className="text-h1 mt-1" data-testid="leaderboard-title">{title}</h1>
      </div>

      <div className="nv-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="leaderboard-filters">
          {(user.access_role !== "House Manager" || isMultiHomeHM) && (
            <div>
              <label className="text-small-caps">Home</label>
              <select value={home} onChange={(e) => setHome(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="lb-home-select">
                <option value="ALL">All Homes</option>
                {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-small-caps">Month</label>
            <select value={month} onChange={(e) => setMonth(+e.target.value)} disabled={view === "annual"} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 disabled:bg-slate-50" data-testid="lb-month-select">
              {FULL_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-small-caps">Year</label>
            <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="lb-year-input" />
          </div>
          <div>
            <label className="text-small-caps">View</label>
            <div className="mt-1.5 inline-flex p-1 bg-slate-100 rounded-md">
              <button onClick={() => setView("monthly")} className={classNames("px-3 py-1.5 text-xs font-semibold rounded transition", view === "monthly" ? "bg-white shadow-sm text-slate-900" : "text-slate-500")} data-testid="lb-view-monthly">Monthly</button>
              <button onClick={() => setView("annual")} className={classNames("px-3 py-1.5 text-xs font-semibold rounded transition", view === "annual" ? "bg-white shadow-sm text-slate-900" : "text-slate-500")} data-testid="lb-view-annual">Annual</button>
            </div>
          </div>
        </div>
      </div>

      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {top3.map((r, i) => (
            <div key={r.staff_user_id} className={classNames(
              "nv-card p-5 relative overflow-hidden",
              i === 0 && "ring-2 ring-amber-300"
            )}>
              {i === 0 && <Crown size={56} className="absolute -top-3 -right-3 text-amber-200 -rotate-12" />}
              <div className="flex items-center gap-3">
                <div className="text-2xl font-heading font-bold text-slate-300 w-7">#{i + 1}</div>
                <Avatar name={r.staff_name} size="lg" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{r.staff_name || "Missing Staff Link"}</p>
                  <p className="text-xs text-slate-500">{r.home}</p>
                </div>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-heading font-bold text-slate-900 tabular-nums">{r.overall_weighted_score?.toFixed(2)}</span>
                <PerformanceBadge band={r.performance_band} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="nv-card overflow-x-auto" data-testid="leaderboard-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 text-left w-16">Rank</th>
              <th className="px-3 py-3 text-left">Staff</th>
              <th className="px-3 py-3 text-left">Home</th>
              <th className="px-3 py-3 text-right">Score</th>
              <th className="px-3 py-3 text-left">Band</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={r.staff_user_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-5 py-3 font-bold text-slate-500 tabular-nums">#{i + 1}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={r.staff_name} size="sm" />
                    <div className="min-w-0">
                      <span className="font-medium text-slate-800 block">{r.staff_name || "Missing Staff Link"}</span>
                      {(r.job_title || r.staff_classification) && <span className="text-[12.5px] text-slate-500">{r.job_title || r.staff_classification}</span>}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-slate-600">{r.home}</td>
                <td className="px-3 py-3 text-right font-semibold text-slate-800 tabular-nums">{r.overall_weighted_score?.toFixed(2)}</td>
                <td className="px-3 py-3"><PerformanceBadge band={r.performance_band} /></td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                <Trophy size={28} className="mx-auto text-slate-300 mb-2" />
                No leaderboard data found for this home, month, and year.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
